import { Check, CircleDashed, CircleMinus, Clock, Hand, MessageCircleQuestion, Pause, X } from 'lucide-react';
import type { ComponentType, SVGProps } from 'react';
import { cn } from '../lib/cn.ts';
import type { StatusTone } from '../lib/status.ts';
import { Tooltip } from './Tooltip.tsx';

/**
 * Every state in the product renders as one of these. Status is always icon plus label,
 * never colour alone, and the colour comes from the status tier of the tokens, which is
 * separate from the brand accent.
 */
export type PillStatus = 'working' | 'needs-you' | 'asked' | 'idle' | 'done' | 'failed' | 'offline' | 'paused' | 'queued' | 'ended';

export const PILL_STATUSES: readonly PillStatus[] = ['working', 'needs-you', 'asked', 'idle', 'done', 'failed', 'offline', 'paused', 'queued', 'ended'];

/** The status colour each state uses: the token color.status.<colour>. */
export type StatusColour = 'working' | 'attention' | 'success' | 'danger' | 'muted' | 'pending' | 'inactive';

type IconType = ComponentType<SVGProps<SVGSVGElement> & { className?: string }>;

/** The working icon: a ring with a turning arc. Static under reduced motion. */
function SpinnerRing({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 16 16" fill="none" className={className} aria-hidden>
      <circle cx="8" cy="8" r="6" stroke="currentColor" strokeOpacity="0.3" strokeWidth="2" />
      <path d="M8 2a6 6 0 0 1 6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="origin-center animate-spin" />
    </svg>
  );
}

/** Hollow dot: idle. */
function HollowDot({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 16 16" fill="none" className={className} aria-hidden>
      <circle cx="8" cy="8" r="4.5" stroke="currentColor" strokeWidth="2" />
    </svg>
  );
}

export interface StatusMeta {
  label: string;
  colour: StatusColour;
  icon: IconType;
  /** Loud states colour their label too; quiet ones keep a muted label and colour only the icon. */
  loud: boolean;
  /** What it means, for tooltips and the catalogue. */
  meaning: string;
}

export const STATUS_META: Record<PillStatus, StatusMeta> = {
  working: { label: 'Working', colour: 'working', icon: SpinnerRing, loud: true, meaning: 'The agent is running.' },
  'needs-you': { label: 'Needs you', colour: 'attention', icon: Hand, loud: true, meaning: 'Waiting on a human decision.' },
  // A turn that ended on a question: nothing is held, so it stays quiet (ADR 0056). The Inbox's question glyph.
  asked: { label: 'Asked', colour: 'muted', icon: MessageCircleQuestion, loud: false, meaning: 'Ended its turn on a question; nothing is held.' },
  idle: { label: 'Idle', colour: 'muted', icon: HollowDot, loud: false, meaning: 'Running, nothing happening right now.' },
  done: { label: 'Done', colour: 'success', icon: Check, loud: true, meaning: 'Finished cleanly.' },
  failed: { label: 'Failed', colour: 'danger', icon: X, loud: true, meaning: 'An error, or blocked.' },
  offline: { label: 'Offline', colour: 'muted', icon: CircleDashed, loud: false, meaning: 'Unreachable: not reporting.' },
  paused: { label: 'Paused', colour: 'muted', icon: Pause, loud: false, meaning: 'Stopped on purpose; resumes when told.' },
  queued: { label: 'Queued', colour: 'pending', icon: Clock, loud: false, meaning: 'Accepted, not started yet.' },
  ended: { label: 'Ended', colour: 'inactive', icon: CircleMinus, loud: false, meaning: 'No longer running. Nothing went wrong.' },
};

/**
 * The quiet fill of a grey pill (idle, queued, ended, offline): a faint wash of the text colour,
 * so it reads as a pill on every surface in both themes (the neutral soft tokens match the dark
 * raised surface and vanish on a drawer header).
 */
const NEUTRAL_SOFT = 'bg-fg/6';

/** Literal class names per status colour (Tailwind needs them spelled out). */
export const STATUS_COLOUR_CLASS: Record<StatusColour, { fg: string; soft: string; line: string }> = {
  working: { fg: 'text-status-working', soft: 'bg-status-working-soft', line: 'border-status-working-line' },
  attention: { fg: 'text-status-attention', soft: 'bg-status-attention-soft', line: 'border-status-attention-line' },
  success: { fg: 'text-status-success', soft: 'bg-status-success-soft', line: 'border-status-success-line' },
  danger: { fg: 'text-status-danger', soft: 'bg-status-danger-soft', line: 'border-status-danger-line' },
  muted: { fg: 'text-status-muted', soft: NEUTRAL_SOFT, line: 'border-border' },
  pending: { fg: 'text-status-pending', soft: NEUTRAL_SOFT, line: 'border-border' },
  inactive: { fg: 'text-status-inactive', soft: NEUTRAL_SOFT, line: 'border-border' },
};

/** Map the existing tones (sessionTone, runTone, pipelineTone...) onto pill states. */
export function pillStatusOf(tone: StatusTone): PillStatus {
  switch (tone) {
    case 'working':
      return 'working';
    case 'waiting':
      return 'needs-you';
    case 'idle':
      return 'idle';
    case 'ended':
      return 'ended';
    case 'passed':
      return 'done';
    case 'failed':
      return 'failed';
    case 'queued':
      return 'queued';
    case 'offline':
      return 'offline';
  }
}

export interface StatusPillProps {
  status: PillStatus;
  /** Override the words (for example "Waiting for approval", "Passed"); the icon and colour stay. */
  label?: string;
  /** soft: tinted pill (default). plain: icon and label only, for rows and dense tables. */
  variant?: 'soft' | 'plain';
  size?: 'sm' | 'md';
  /** Icon only, with the label as tooltip and accessible name. Only where space is tight. */
  iconOnly?: boolean;
  className?: string;
}

/** Icon plus label plus colour, for every status everywhere. */
export function StatusPill({ status, label, variant = 'soft', size = 'sm', iconOnly, className }: StatusPillProps) {
  const meta = STATUS_META[status];
  const c = STATUS_COLOUR_CLASS[meta.colour];
  const text = label ?? meta.label;
  const Icon = meta.icon;
  const icon = <Icon className={cn('size-(--pill-icon-size) shrink-0', c.fg)} aria-hidden />;

  if (iconOnly) {
    return (
      <Tooltip content={text}>
        <span role="img" aria-label={text} data-status={status} tabIndex={0} className={cn('inline-flex shrink-0 items-center justify-center rounded-pill p-0.5', className)}>
          {icon}
        </span>
      </Tooltip>
    );
  }

  return (
    <span
      data-status={status}
      className={cn(
        'inline-flex max-w-full items-center gap-(--pill-gap) whitespace-nowrap rounded-(--pill-radius) font-medium',
        size === 'sm' ? 'h-(--pill-height) text-caption' : 'h-(--pill-height-md) text-ui',
        variant === 'soft' ? cn('px-(--pill-padding-x)', c.soft) : 'px-0',
        meta.loud ? c.fg : 'text-fg-muted',
        className,
      )}
    >
      {icon}
      <span className="truncate">{text}</span>
    </span>
  );
}
